import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { useMinimalCustomers } from "@/api/hooks/useCustomers/useMinimalCustomers";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";

import { useContext, useMemo, useState } from "react";

import { CardView } from "../../../../components/v2/CardView";
import { HomePageContext } from "../../context";
import { LeaderboardRow } from "./components/leaderboardRow";
import { TabsWithSubtitle } from "./components/tabsWithSubtitle";
import { METRIC_CONFIG } from "./constants";
import { Direction, Metric } from "./types";
import { getCustomersWithoutCleaners, getLeaderboardRows } from "./utils";

export const Leaderboard: React.FC = () => {
  const { timeFilter, comparisonTimeFilter, selectedTenantCategoryIds } =
    useContext(HomePageContext);
  const { currentTerminal } = useTerminalsState();
  const { minimalCustomerList } = useMinimalCustomers();
  const [metric, setMetric] = useState<Metric>(Metric.RECYCLING_RATE);
  const [direction, setDirection] = useState<Direction>(Direction.TOP);
  const [hideFullRate, setHideFullRate] = useState<boolean>(false);

  const cfg = useMemo(() => METRIC_CONFIG[metric], [metric]);

  const { data, isLoading } = trpc.dwh.customers.get.useQuery(
    {
      ...(timeFilter || defaultTimeFilter),
      terminalId: currentTerminal?.id || "",
      tenantCategories: selectedTenantCategoryIds || [],
    },
    {
      queryKeyHashFn: () =>
        [
          "customers",
          currentTerminal?.id,
          timeFilter?.startTimeISO,
          timeFilter?.endTimeISO,
          selectedTenantCategoryIds,
        ].toString(),
      enabled: !!timeFilter && !!currentTerminal,
    },
  );

  const { data: comparisonData, isLoading: isLoadingComparison } =
    trpc.dwh.customers.get.useQuery(
      {
        ...(comparisonTimeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "customersComparison",
            currentTerminal?.id,
            comparisonTimeFilter?.startTimeISO,
            comparisonTimeFilter?.endTimeISO,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!currentTerminal && !!comparisonTimeFilter,
      },
    );

  const showLoadingState = useMemo(
    () => isLoading || isLoadingComparison,
    [isLoading, isLoadingComparison],
  );

  const filters = useMemo(
    () => [
      {
        options: [
          { key: Direction.TOP, label: "homePage:leaderboardTop5" },
          { key: Direction.BOTTOM, label: "homePage:leaderboardBottom5" },
        ],
        value: direction,
        onChange: (key: string) => setDirection(key as Direction),
      },
    ],
    [direction],
  );

  const rows = useMemo(
    () =>
      getLeaderboardRows({
        currentCustomers: getCustomersWithoutCleaners({
          data,
          minimalCustomerList,
        }),
        comparisonCustomers: getCustomersWithoutCleaners({
          data: comparisonData,
          minimalCustomerList,
        }),
        metric,
        direction,
        hideFullRate,
      }),
    [
      metric,
      direction,
      hideFullRate,
      data,
      comparisonData,
      minimalCustomerList,
    ],
  );

  return (
    <CardView
      title="homePage:leaderboardTitle"
      subtitle="homePage:leaderboardSubtitle"
      filters={filters}
      isLoading={showLoadingState}
    >
      <ViewWithLoader isLoading={showLoadingState}>
        <TabsWithSubtitle
          metric={metric}
          setMetric={setMetric}
          direction={direction}
          hideFullRate={hideFullRate}
          setHideFullRate={setHideFullRate}
        />

        <div className="@container/lb mt-[4.95px] flex flex-col">
          {rows.map(({ tenant, rank, top }, i) => (
            <LeaderboardRow
              key={tenant.id}
              tenant={tenant}
              index={i}
              rank={rank}
              top={top}
              cfg={cfg}
            />
          ))}
        </div>
      </ViewWithLoader>
    </CardView>
  );
};
